import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { WeaveCheckbox } from '../WeaveCheckbox/WeaveCheckbox';
import styles from './styles.module.scss';

interface MultiSelectProps {
	options: { label: string; value: string }[];
	selectedValues: string[];
	onChange: (selectedValues: string[]) => void;
	disabled?: boolean;
	width?: string;
}

export const MultiSelect = ({
	options,
	selectedValues,
	onChange,
	disabled,
	width,
}: MultiSelectProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
	const [displayText, setDisplayText] = useState('Select options...');
	const buttonRef = useRef<HTMLButtonElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const textRef = useRef<HTMLSpanElement>(null);

	const handleCheckboxChange = (value: string, checked: boolean) => {
		if (checked) {
			onChange([...selectedValues, value]);
		} else {
			onChange(selectedValues.filter((v) => v !== value));
		}
	};

	useEffect(() => {
		const updateDisplay = () => {
			if (selectedValues.length === 0) {
				setDisplayText('Select options...');
				return;
			}

			const selectedOptions = selectedValues
				.map((val) => options.find((opt) => opt.value === val))
				.filter(Boolean)
				.map((opt) => opt!.label);

			if (selectedOptions.length === 1) {
				setDisplayText(selectedOptions[0]);
				return;
			}

			if (!buttonRef.current) {
				setDisplayText(`${selectedValues.length} selected`);
				return;
			}

			const buttonWidth = buttonRef.current.offsetWidth;
			const arrowWidth = 24;
			const availableWidth = buttonWidth - arrowWidth - 24;

			const tempSpan = document.createElement('span');
			tempSpan.style.visibility = 'hidden';
			tempSpan.style.position = 'absolute';
			tempSpan.style.fontSize = '10px';
			tempSpan.style.fontFamily = getComputedStyle(buttonRef.current).fontFamily;
			document.body.appendChild(tempSpan);

			let text = selectedOptions.join(', ');
			tempSpan.textContent = text;
			let textWidth = tempSpan.offsetWidth;

			if (textWidth <= availableWidth) {
				document.body.removeChild(tempSpan);
				setDisplayText(text);
				return;
			}

			for (let i = selectedOptions.length - 1; i > 0; i--) {
				const remaining = selectedValues.length - i;
				text = selectedOptions.slice(0, i).join(', ') + `, +${remaining} more`;
				tempSpan.textContent = text;
				textWidth = tempSpan.offsetWidth;

				if (textWidth <= availableWidth) {
					document.body.removeChild(tempSpan);
					setDisplayText(text);
					return;
				}
			}

			document.body.removeChild(tempSpan);
			setDisplayText(`${selectedValues.length} selected`);
		};

		updateDisplay();
	}, [selectedValues, options]);

	useEffect(() => {
		if (isOpen && buttonRef.current) {
			const rect = buttonRef.current.getBoundingClientRect();
			setDropdownPosition({
				top: rect.bottom + 4,
				left: rect.left,
				width: rect.width,
			});
		}
	}, [isOpen]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node) &&
				buttonRef.current &&
				!buttonRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen]);

	return (
		<>
			<div className={styles.multiSelect} style={{ width: width }}>
				<button
					ref={buttonRef}
					type="button"
					className={styles.selectButton}
					onClick={() => !disabled && setIsOpen(!isOpen)}
					disabled={disabled}
				>
					<span ref={textRef} className={styles.selectText}>
						{displayText}
					</span>
					<span className={`${styles.arrow} ${isOpen ? styles.arrowUp : ''}`}>▼</span>
				</button>
			</div>
			{isOpen &&
				createPortal(
					<div
						ref={dropdownRef}
						className={styles.dropdown}
						style={{
							top: `${dropdownPosition.top}px`,
							left: `${dropdownPosition.left}px`,
							width: `${dropdownPosition.width}px`,
						}}
					>
						{options.map((option) => (
							<div key={option.value} className={styles.option}>
								<WeaveCheckbox
									checked={selectedValues.includes(option.value)}
									label={option.label}
									showLabel={true}
									disabled={disabled}
									change={() =>
										handleCheckboxChange(
											option.value,
											!selectedValues.includes(option.value)
										)
									}
								/>
							</div>
						))}
					</div>,
					document.body
				)}
		</>
	);
};
